import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import ConfirmDialog from './ConfirmDialog'
import { brl, ptDate } from './finance-shared'
import { PanelShell, PlnBarra, PlnBarraRot, PlnBloco, stickyTh } from './PanelShell'
import {
  FiUploadCloud, FiLoader, FiCheckCircle, FiAlertTriangle, FiChevronLeft, FiChevronRight,
  FiCopy, FiCheck, FiInbox, FiArrowLeft, FiTrash2,
} from 'react-icons/fi'

// Painel DDA (PRD §5.7) — versão planilha: o diretor exporta o "DDAObrigacoes"
// do TotalBank e importa aqui; cada boleto novo vira despesa em aberto no
// Contas a pagar (reference_code='DDA'). Reimportar o mesmo arquivo não
// duplica (o backend deduplica pela linha digitável).

interface DdaRow {
  id: number
  beneficiario_doc: string | null
  beneficiario_nome: string | null
  doc_empresa: string | null
  vencimento: string
  status_dda: string | null
  valor_documento: string
  valor_pagamento: string | null
  linha_digitavel: string | null
  categoria: string | null
  txn_status: 'em_aberto' | 'agendado' | 'pago' | 'perda' | null
  txn_payment_date: string | null
}

interface Cards {
  total: { n: number; valor: number }
  a_exportar: { n: number; valor: number }
  em_aberto: { n: number; valor: number }
  vencidos: { n: number; valor: number }
  pagos: { n: number; valor: number }
}

interface ParseRow {
  __line: number
  beneficiario_doc: string | null
  beneficiario_nome: string | null
  doc_empresa: string | null
  vencimento: string
  status_dda: string | null
  valor_documento: number
  valor_pagamento: number | null
  linha_digitavel: string | null
  dedup_key: string
  duplicate: boolean
  categoria_sugerida: { id: number; name: string } | null
}

interface ParseResp {
  success: boolean
  error?: string
  rows: ParseRow[]
  invalid: { line: number; error: string }[]
  resumo: { total: number; novos: number; duplicados: number; valor_novos: number }
}

interface CommitResp { success: boolean; error?: string; imported: number; skipped: number; failed: { line: number | string; error: string }[] }

const mesLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  const s = new Date(y, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  return s.charAt(0).toUpperCase() + s.slice(1) // "Maio de 2026" (não capitalizar o "de")
}
const shiftMonth = (ym: string, delta: number) => {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
const hojeYM = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
const hojeISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function situacao(r: DdaRow): { label: string; cls: string } {
  if (r.txn_status === 'pago') return { label: 'Pago', cls: 'neu-tag bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' }
  if (r.txn_status === 'perda') return { label: 'Perda', cls: 'neu-tag bg-muted text-muted-foreground' }
  if (!r.txn_status) return { label: 'Não exportado', cls: 'neu-tag bg-amber-500/15 text-amber-600 dark:text-amber-400' }
  if (String(r.vencimento).slice(0, 10) < hojeISO()) return { label: 'Vencido', cls: 'neu-tag bg-red-500/15 text-red-600 dark:text-red-400' }
  return { label: 'Em aberto', cls: 'neu-tag bg-sky-500/15 text-sky-600 dark:text-sky-400' }
}

export function DdaPanel() {
  const { authenticatedFetch } = useAuth()
  const [month, setMonth] = useState(hojeYM())
  const [rows, setRows] = useState<DdaRow[]>([])
  const [cards, setCards] = useState<Cards | null>(null)
  const [loading, setLoading] = useState(true)
  const [importOpen, setImportOpen] = useState(false)
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [toRemove, setToRemove] = useState<DdaRow | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  // Seleção em lote (excluir vários / exportar pro Contas a pagar)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkMsg, setBulkMsg] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await authenticatedFetch(`/api/dda?month=${month}`)
      const d = await r.json()
      if (d.success) { setRows(d.rows); setCards(d.cards); setSelected(new Set()) }
    } catch { /* mantém estado anterior */ }
    finally { setLoading(false) }
  }, [authenticatedFetch, month])

  useEffect(() => { load() }, [load])

  const removeBoleto = async () => {
    if (!toRemove) return
    setRemoveError(null)
    try {
      const r = await authenticatedFetch(`/api/dda/${toRemove.id}`, { method: 'DELETE' })
      const d = await r.json()
      if (!d.success) { setRemoveError(d.error || 'Falha ao remover'); return }
      setToRemove(null)
      load()
    } catch { setRemoveError('Erro ao remover o boleto') }
  }

  // Seleção: pago não entra (não pode ser excluído nem re-exportado)
  const selectable = useMemo(() => rows.filter(r => r.txn_status !== 'pago'), [rows])
  const exportaveis = useMemo(() => [...selected].filter(id => rows.find(r => r.id === id && !r.txn_status)), [selected, rows])
  const allSelected = selectable.length > 0 && selectable.every(r => selected.has(r.id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map(r => r.id)))
  const toggleOne = (id: number) => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const exportSelected = async () => {
    if (!exportaveis.length || bulkBusy) return
    setBulkBusy(true); setBulkMsg(null)
    try {
      const r = await authenticatedFetch('/api/dda/export', { method: 'POST', body: JSON.stringify({ ids: exportaveis }) })
      let d = await r.json()
      if (!d.success) { setBulkMsg(`⚠ ${d.error || 'Falha ao exportar'}`); return }

      // Guardrail (2026-07-22): boletos cuja despesa já existe (mesmo
      // beneficiário, vencimento e valor) não são exportados de imediato —
      // pergunta antes, porque cobrança recorrente do mesmo fornecedor é
      // legítima e não pode ser barrada em silêncio.
      if (d.duplicados?.length) {
        const lista = d.duplicados.slice(0, 8)
          .map((x: any) => `· ${x.beneficiario} — ${brl(x.valor)} venc. ${ptDate(x.vencimento)}`).join('\n')
        const resto = d.duplicados.length > 8 ? `\n… e mais ${d.duplicados.length - 8}` : ''
        const ok = confirm(
          `${d.duplicados.length} boleto(s) já têm despesa equivalente lançada:\n\n${lista}${resto}\n\n` +
          `Exportar assim mesmo? (Cancelar deixa esses de fora — os demais já foram exportados.)`)
        if (ok) {
          const r2 = await authenticatedFetch('/api/dda/export', {
            method: 'POST',
            body: JSON.stringify({ ids: d.duplicados.map((x: any) => x.id), confirmar_duplicados: true }),
          })
          const d2 = await r2.json()
          if (d2.success) d = { ...d, exported: (d.exported || 0) + (d2.exported || 0), duplicados: [] }
          else { setBulkMsg(`⚠ ${d2.error || 'Falha ao exportar os repetidos'}`); load(); return }
        }
      }

      setBulkMsg(`${d.exported} despesa${d.exported === 1 ? '' : 's'} criada${d.exported === 1 ? '' : 's'} no Contas a pagar`
        + `${d.failed?.length ? ` · ${d.failed.length} com erro` : ''}`
        + `${d.duplicados?.length ? ` · ${d.duplicados.length} já existia(m) e ficou(aram) de fora` : ''}`)
      load()
    } catch { setBulkMsg('⚠ Erro ao exportar boletos') }
    finally { setBulkBusy(false) }
  }

  const bulkRemove = async () => {
    if (!selected.size || bulkBusy) return
    setBulkBusy(true); setBulkMsg(null)
    try {
      const r = await authenticatedFetch('/api/dda/remove', { method: 'POST', body: JSON.stringify({ ids: [...selected] }) })
      const d = await r.json()
      if (!d.success) { setBulkMsg(`⚠ ${d.error || 'Falha ao remover'}`); return }
      setBulkMsg(`${d.removed} boleto${d.removed === 1 ? '' : 's'} removido${d.removed === 1 ? '' : 's'}${d.skippedPagos ? ` · ${d.skippedPagos} pago${d.skippedPagos === 1 ? '' : 's'} mantido${d.skippedPagos === 1 ? '' : 's'}` : ''}`)
      setBulkRemoveOpen(false)
      load()
    } catch { setBulkMsg('⚠ Erro ao remover boletos') }
    finally { setBulkBusy(false) }
  }

  const copyLinha = async (r: DdaRow) => {
    if (!r.linha_digitavel) return
    try {
      await navigator.clipboard.writeText(r.linha_digitavel)
      setCopiedId(r.id)
      setTimeout(() => setCopiedId(c => (c === r.id ? null : c)), 1500)
    } catch { /* clipboard bloqueado */ }
  }

  const cardDefs = cards ? [
    { label: 'Boletos no mês', n: cards.total.n, valor: cards.total.valor, cls: 'text-foreground' },
    { label: 'A exportar', n: cards.a_exportar.n, valor: cards.a_exportar.valor, cls: 'text-amber-600 dark:text-amber-400' },
    { label: 'Em aberto', n: cards.em_aberto.n, valor: cards.em_aberto.valor, cls: 'text-sky-600 dark:text-sky-400' },
    { label: 'Vencidos', n: cards.vencidos.n, valor: cards.vencidos.valor, cls: 'text-red-600 dark:text-red-400' },
    { label: 'Pagos', n: cards.pagos.n, valor: cards.pagos.valor, cls: 'text-emerald-600 dark:text-emerald-400' },
  ] : []

  return (
    // PLANOS (21/10/2026): cabeçalho com título/sub/ícone; "Importar DDA" foi
    // para `acoes`; o navegador de mês mora numa PlnBarra; a tabela, num
    // PlnBloco (a contagem e o retorno do lote viraram nota/extra do bloco).
    <PanelShell
      titulo="DDA"
      sub="Boletos do DDA do TotalBank: conferir o mês e mandar para o Contas a pagar"
      icone={<FiInbox />}
      acoes={
        <button onClick={() => setImportOpen(true)} title="Importar o export DDAObrigacoes do TotalBank"
          className="neu-relief inline-flex items-center gap-2 h-9 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3.5 hover:opacity-90 transition-opacity">
          <FiUploadCloud className="w-4 h-4" /> Importar DDA
        </button>
      }
      toolbar={
      /* ── Barra: navegação de mês ── */
      <PlnBarra>
        <PlnBarraRot>Vencimento em</PlnBarraRot>
        {/* Navegador de mês — mesmo padrão do Fluxo de caixa (pedido 2026-07-20) */}
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(m => shiftMonth(m, -1))} title="Anterior"
            className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary">
            <FiChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-foreground min-w-[140px] text-center">{mesLabel(month)}</span>
          <button onClick={() => setMonth(m => shiftMonth(m, 1))} title="Próximo"
            className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary">
            <FiChevronRight className="w-4 h-4" />
          </button>
        </div>
        {month !== hojeYM() && (
          <button onClick={() => setMonth(hojeYM())} title="Voltar ao mês atual"
            className="neu-convex h-8 px-2.5 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            Hoje
          </button>
        )}
      </PlnBarra>
    }>
      <div className="flex-1 min-h-0 flex flex-col gap-4 pln-corpo-tela">
      {/* ── Cards ── */}
      {cards && (
        <div className="shrink-0 grid grid-cols-2 md:grid-cols-5 neu-card rounded-xl border border-border bg-card divide-x divide-border/60">
          {cardDefs.map(c => (
            // PLANOS: cartão já era caixa — só ganhou respiro e letra (rótulo 13, valor 18).
            <div key={c.label} className="px-4 py-3.5">
              <div className="text-[13px] font-medium text-muted-foreground">{c.label}</div>
              <div className={`text-lg font-semibold tabular-nums mt-0.5 ${c.n ? c.cls : 'text-muted-foreground/50'}`}>{brl(c.valor)}</div>
              <div className="text-xs text-muted-foreground tabular-nums">{c.n} boleto{c.n === 1 ? '' : 's'}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Barra de lote (aparece com seleção) + feedback ── */}
      {selected.size > 0 && (
        <div className="neu-relief shrink-0 flex items-center gap-2 flex-wrap rounded-xl border border-primary/30 bg-primary/5 px-3 py-2">
          <span className="text-xs font-medium text-foreground tabular-nums">{selected.size} selecionado{selected.size === 1 ? '' : 's'}</span>
          <button onClick={exportSelected} disabled={bulkBusy || exportaveis.length === 0}
            title={exportaveis.length === 0 ? 'Nenhum dos selecionados está pendente de exportação' : `Criar ${exportaveis.length} despesa(s) em aberto no Contas a pagar`}
            className="neu-relief inline-flex items-center gap-1.5 h-7 rounded-lg bg-primary text-primary-foreground text-xs font-medium px-2.5 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity">
            {bulkBusy ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheckCircle className="w-3 h-3" />}
            Exportar p/ Contas a pagar{exportaveis.length ? ` (${exportaveis.length})` : ''}
          </button>
          <button onClick={() => setBulkRemoveOpen(true)} disabled={bulkBusy}
            title="Remover os boletos selecionados (apaga despesas em aberto vinculadas)"
            className="neu-relief inline-flex items-center gap-1.5 h-7 rounded-lg border border-red-500/40 text-red-600 dark:text-red-400 text-xs font-medium px-2.5 hover:bg-red-500/10 disabled:opacity-40 transition-colors">
            <FiTrash2 className="w-3 h-3" /> Excluir ({selected.size})
          </button>
          <button onClick={() => setSelected(new Set())} disabled={bulkBusy}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors">
            Limpar seleção
          </button>
        </div>
      )}
      {/* ── Tabela — única região com scroll; cabeçalho sticky. min-h = janela
          útil no celular (box degrada pro scroll de página se precisar) ── */}
      <PlnBloco titulo="Boletos"
        nota={loading ? undefined : `${rows.length} boleto${rows.length === 1 ? '' : 's'} com vencimento em ${mesLabel(month)}`}
        extra={bulkMsg ? <span className="text-xs text-muted-foreground">{bulkMsg}</span> : undefined}
        className="flex-1 min-h-[280px]">
      <div className="neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
        {loading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center p-10 text-center text-muted-foreground">
            <FiInbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhum boleto DDA com vencimento em {mesLabel(month)}.</p>
            <p className="text-xs mt-1">Use "Importar DDA" com o export DDAObrigacoes do TotalBank.</p>
          </div>
        ) : (
          <table className="w-full text-xs min-w-[860px]">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className={`pl-4 pr-1 py-2 w-8 ${stickyTh}`}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                    title="Selecionar todos (boletos pagos ficam de fora)"
                    className="neu-check accent-[hsl(var(--primary))] cursor-pointer" />
                </th>
                <th className={`px-3 py-2 font-medium ${stickyTh}`}>Vencimento</th>
                <th className={`px-3 py-2 font-medium ${stickyTh}`}>Favorecido</th>
                <th className={`px-3 py-2 font-medium hidden lg:table-cell ${stickyTh}`}>Doc empresa</th>
                <th className={`px-3 py-2 font-medium hidden xl:table-cell ${stickyTh}`}>Categoria</th>
                <th className={`px-3 py-2 font-medium text-right ${stickyTh}`}>Valor</th>
                <th className={`px-3 py-2 font-medium text-right hidden lg:table-cell ${stickyTh}`}>Valor atualizado</th>
                <th className={`px-3 py-2 font-medium ${stickyTh}`}>Situação</th>
                <th className={`px-3 py-2 font-medium text-center ${stickyTh}`} title="Exportado pro Contas a pagar">Exportado</th>
                <th className={`px-3 py-2 font-medium text-right ${stickyTh}`}>Linha digitável</th>
                <th className={`px-3 py-2 pr-4 font-medium ${stickyTh}`}><span className="sr-only">Ações</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const sit = situacao(r)
                return (
                  <tr key={r.id} className={`neu-tr border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors ${selected.has(r.id) ? 'neu-tr-on' : ''}`}>
                    <td className="pl-4 pr-1 py-2">
                      {r.txn_status === 'pago' ? (
                        <input type="checkbox" disabled title="Boleto pago não entra em ações de lote" className="neu-check opacity-30 cursor-not-allowed" />
                      ) : (
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)}
                          className="neu-check accent-[hsl(var(--primary))] cursor-pointer" />
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">{ptDate(String(r.vencimento).slice(0, 10))}</td>
                    <td className="px-3 py-2 min-w-0">
                      <div className="truncate max-w-[260px] font-medium text-foreground">{r.beneficiario_nome || '—'}</div>
                      {r.beneficiario_doc && <div className="text-[10px] text-muted-foreground tabular-nums">{r.beneficiario_doc}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap hidden lg:table-cell text-muted-foreground">{r.doc_empresa || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap hidden xl:table-cell text-muted-foreground truncate max-w-[160px]">{r.categoria || '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums font-medium">{brl(r.valor_documento)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums hidden lg:table-cell text-muted-foreground">
                      {r.valor_pagamento != null && Number(r.valor_pagamento) !== Number(r.valor_documento) ? brl(r.valor_pagamento) : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${sit.cls}`}>{sit.label}</span>
                      {r.txn_status === 'pago' && r.txn_payment_date && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground tabular-nums">{ptDate(String(r.txn_payment_date).slice(0, 10))}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.txn_status ? (
                        <span title="Exportado pro Contas a pagar — a despesa aparece lá com o selo DDA"
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 dark:text-violet-400">
                          <FiCheckCircle className="w-3.5 h-3.5" /> CP
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40" title="Ainda não exportado — selecione e use 'Exportar p/ Contas a pagar'">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.linha_digitavel ? (
                        <button onClick={() => copyLinha(r)} title={`Copiar linha digitável\n${r.linha_digitavel}`}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] transition-colors ${
                            copiedId === r.id
                              ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                              : 'border-border text-muted-foreground hover:text-foreground hover:bg-secondary'
                          }`}>
                          {copiedId === r.id ? <FiCheck className="w-3 h-3" /> : <FiCopy className="w-3 h-3" />}
                          {copiedId === r.id ? 'Copiada' : 'Copiar'}
                        </button>
                      ) : <span className="text-muted-foreground/50">—</span>}
                    </td>
                    <td className="px-3 py-2 pr-4 whitespace-nowrap text-right">
                      {r.txn_status === 'pago' ? (
                        <span className="inline-flex p-1 text-muted-foreground/30" title="Boleto pago não pode ser removido">
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <button onClick={() => { setRemoveError(null); setToRemove(r) }}
                          title="Remover boleto (apaga também a despesa em aberto vinculada)"
                          className="neu-relief inline-flex p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
      </PlnBloco>
      </div>

      {importOpen && <ImportarDda onClose={() => setImportOpen(false)} onImported={ym => { setImportOpen(false); if (ym) setMonth(ym); load() }} />}

      <ConfirmDialog
        isOpen={!!toRemove}
        title="Remover boleto DDA"
        message={toRemove ? `Remover o boleto de ${toRemove.beneficiario_nome || 'favorecido'} (${brl(toRemove.valor_documento)}, vence ${ptDate(String(toRemove.vencimento).slice(0, 10))})?` : ''}
        details={[
          'A despesa em aberto vinculada no Contas a pagar também será apagada.',
          'Reimportar o extrato DDA não traz este boleto de volta.',
          ...(removeError ? [`⚠ ${removeError}`] : []),
        ]}
        confirmText="Remover"
        onConfirm={removeBoleto}
        onCancel={() => { setToRemove(null); setRemoveError(null) }}
      />

      <ConfirmDialog
        isOpen={bulkRemoveOpen}
        title="Remover boletos selecionados"
        message={`Remover ${selected.size} boleto${selected.size === 1 ? '' : 's'} da lista do DDA?`}
        details={[
          'Despesas em aberto vinculadas no Contas a pagar também serão apagadas.',
          'Reimportar o extrato DDA não traz estes boletos de volta.',
        ]}
        confirmText={`Remover ${selected.size}`}
        onConfirm={bulkRemove}
        onCancel={() => setBulkRemoveOpen(false)}
      />
    </PanelShell>
  )
}

// ── Wizard de import (Carregar → Conferir → Concluir) ─────────────────
// Layout do arquivo é fixo (TotalBank), então não há etapa de mapear colunas.
function ImportarDda({ onClose, onImported }: { onClose: () => void; onImported: (ym: string | null) => void }) {
  const { authenticatedFetch } = useAuth()
  const [step, setStep] = useState(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fileName, setFileName] = useState('')
  const [parsed, setParsed] = useState<ParseResp | null>(null)
  const [result, setResult] = useState<CommitResp | null>(null)

  async function handleFile(file: File) {
    setBusy(true); setError(null)
    try {
      const fd = new FormData(); fd.append('file', file)
      const r = await authenticatedFetch('/api/dda/parse', { method: 'POST', body: fd })
      const d: ParseResp = await r.json()
      if (!d.success) { setError(d.error || 'Falha ao ler o arquivo'); return }
      setFileName(file.name)
      setParsed(d)
      setStep(2)
    } catch { setError('Erro ao enviar o arquivo') }
    finally { setBusy(false) }
  }

  const novos = useMemo(() => (parsed?.rows || []).filter(r => !r.duplicate), [parsed])

  async function commit() {
    if (!parsed) return
    setBusy(true); setError(null)
    try {
      const r = await authenticatedFetch('/api/dda/commit', { method: 'POST', body: JSON.stringify({ rows: novos }) })
      const d: CommitResp = await r.json()
      if (!d.success) { setError(d.error || 'Falha ao importar'); return }
      setResult(d)
      setStep(3)
    } catch { setError('Erro ao importar boletos') }
    finally { setBusy(false) }
  }

  const steps = ['Carregar', 'Conferir', 'Concluir']
  // mês predominante dos novos — pro painel já abrir no mês certo após importar
  const mesImportado = novos.length ? String(novos[0].vencimento).slice(0, 7) : null

  const footer = (
    <>
      {step === 2 && (
        <button onClick={() => { setError(null); setStep(1) }} disabled={busy}
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary disabled:opacity-40">
          <FiArrowLeft className="w-4 h-4" /> Voltar
        </button>
      )}
      {step === 2 && (
        <button onClick={commit} disabled={busy || novos.length === 0}
          className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheckCircle className="w-4 h-4" />}
          Importar {novos.length} boleto{novos.length === 1 ? '' : 's'}
        </button>
      )}
      {step === 3 && (
        <button onClick={() => onImported(mesImportado)} className="neu-relief rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90">Fechar</button>
      )}
    </>
  )

  return (
    <SideModal title="Importar DDA" subtitle={fileName || 'Export "DDAObrigacoes" do TotalBank (.xlsx)'}
      icon={FiUploadCloud} onClose={onClose} maxWidth="max-w-3xl" footer={footer}>
      <div className="flex items-center gap-1.5 text-xs mb-2">
        {steps.map((s, i) => {
          const n = i + 1, done = n < step, cur = n === step
          return (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-semibold ${done ? 'bg-primary text-primary-foreground' : cur ? 'bg-primary/15 text-primary ring-1 ring-primary' : 'bg-secondary text-muted-foreground'}`}>{n}</span>
              <span className={cur ? 'text-foreground font-medium' : 'text-muted-foreground'}>{s}</span>
              {i < steps.length - 1 && <span className="w-4 h-px bg-border mx-0.5" />}
            </div>
          )
        })}
      </div>

      {error && <div className="text-sm text-red-500 flex items-center gap-2"><FiAlertTriangle className="w-4 h-4 shrink-0" />{error}</div>}

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            No TotalBank, exporte o relatório <span className="font-medium text-foreground">DDAObrigacoes</span> (Excel)
            e envie o arquivo aqui. Os boletos entram na lista do DDA como <span className="font-medium text-foreground">"Não exportado"</span> —
            depois é só selecionar os que interessam e <span className="font-medium text-foreground">exportar pro Contas a pagar</span> (ou
            excluir os que não). Boletos já importados são reconhecidos e pulados.
          </p>
          <label className="neu-well block border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors">
            <input type="file" accept=".xls,.xlsx" className="hidden" disabled={busy}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
            {busy ? <FiLoader className="w-8 h-8 mx-auto animate-spin text-muted-foreground" /> : <FiUploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />}
            <div className="mt-2 text-sm text-foreground font-medium">Clique para selecionar o arquivo</div>
            <div className="text-xs text-muted-foreground mt-1">DDAObrigacoes.xlsx — o layout é reconhecido automaticamente.</div>
          </label>
        </div>
      )}

      {step === 2 && parsed && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 rounded-lg border border-border divide-x divide-border/60 text-center">
            <div className="px-3 py-2">
              <div className="text-[10px] uppercase text-muted-foreground">Novos</div>
              <div className="text-sm font-semibold tabular-nums">{parsed.resumo.novos} · {brl(parsed.resumo.valor_novos)}</div>
            </div>
            <div className="px-3 py-2">
              <div className="text-[10px] uppercase text-muted-foreground">Já importados</div>
              <div className="text-sm font-semibold tabular-nums text-muted-foreground">{parsed.resumo.duplicados}</div>
            </div>
            <div className="px-3 py-2">
              <div className="text-[10px] uppercase text-muted-foreground">Linhas inválidas</div>
              <div className={`text-sm font-semibold tabular-nums ${parsed.invalid.length ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>{parsed.invalid.length}</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            A categoria vale na hora de <span className="font-medium text-foreground">exportar pro Contas a pagar</span>:
            sem sugestão, entra na categoria <span className="font-medium text-foreground">DDA</span> (reclassificável depois).
          </p>
          <div className="border border-border rounded-lg overflow-x-auto max-h-[46vh]">
            <table className="w-full text-xs min-w-[560px]">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-2 py-1.5 font-medium">Vencimento</th>
                  <th className="px-2 py-1.5 font-medium">Favorecido</th>
                  <th className="px-2 py-1.5 font-medium text-right">Valor</th>
                  <th className="px-2 py-1.5 font-medium">Categoria</th>
                  <th className="px-2 py-1.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.map((r, i) => (
                  <tr key={i} className={`border-b border-border/50 last:border-0 ${r.duplicate ? 'opacity-45' : ''}`}>
                    <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{ptDate(r.vencimento)}</td>
                    <td className="px-2 py-1.5 truncate max-w-[240px]">{r.beneficiario_nome}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">{brl(r.valor_documento)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap truncate max-w-[140px]">{r.categoria_sugerida?.name || 'DDA'}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-[10px] text-muted-foreground">{r.duplicate ? 'já importado' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {parsed.invalid.length > 0 && (
            <div className="text-xs text-amber-600 dark:text-amber-400">
              {parsed.invalid.length} linha{parsed.invalid.length === 1 ? '' : 's'} sem vencimento/favorecido/valor válido ser{parsed.invalid.length === 1 ? 'á' : 'ão'} ignorada{parsed.invalid.length === 1 ? '' : 's'}.
            </div>
          )}
        </div>
      )}

      {step === 3 && result && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <FiCheckCircle className="w-8 h-8 text-primary shrink-0" />
            <div>
              <div className="text-lg font-semibold text-foreground">
                {result.imported} boleto{result.imported === 1 ? '' : 's'} importado{result.imported === 1 ? '' : 's'} — selecione e exporte pro Contas a pagar
              </div>
              <div className="text-sm text-muted-foreground">
                {result.skipped > 0 && <>{result.skipped} boleto{result.skipped === 1 ? '' : 's'} já importado{result.skipped === 1 ? '' : 's'} (pulado{result.skipped === 1 ? '' : 's'}). </>}
                {result.failed.length > 0 && <span className="text-amber-600 dark:text-amber-400">{result.failed.length} com erro (abaixo).</span>}
              </div>
            </div>
          </div>
          {result.failed.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-secondary/40 border-b border-border">Linhas não importadas</div>
              <div className="max-h-[40vh] overflow-auto divide-y divide-border/50">
                {result.failed.map((f, i) => (
                  <div key={i} className="px-3 py-2 text-sm flex gap-2">
                    <span className="text-muted-foreground shrink-0">Linha {f.line}:</span>
                    <span className="text-foreground">{f.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </SideModal>
  )
}
